import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, switchMap, filter, map, startWith } from 'rxjs';
import { B2bUnitConnector } from '@b2b/features/b2b-unit/core/b2b-unit.connector';
import { B2BUnitService } from '@b2b/features/b2b-unit/core/b2b-unit.service';
import { B2BUnitAddress } from '@b2b/features/b2b-unit/core/b2b-unit.model';
import { CheckoutStateService } from '@b2b/features/cart/core/checkout-state.service';

interface AddressState {
  loaded: boolean;
  data: B2BUnitAddress | null;
}

@Component({
  selector: 'checkout-delivery-address',
  imports: [CommonModule, TranslateModule],
  templateUrl: './checkout-delivery-address.component.html',
  styleUrl: './checkout-delivery-address.component.scss',
})
export class CheckoutDeliveryAddressComponent implements OnInit {
  addressState$!: Observable<AddressState>;

  constructor(
    private b2bUnitService: B2BUnitService,
    private b2bUnitConnector: B2bUnitConnector,
    private checkoutState: CheckoutStateService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.addressState$ = this.b2bUnitService.getCurrentB2BUnit().pipe(
      filter(state => !!state.data?.uid),
      map(state => state.data!.uid),
      switchMap(uid => this.b2bUnitConnector.getB2BUnitAddress(uid)),
      map(response => ({ loaded: true, data: response.data ?? null }) as AddressState),
      startWith({ loaded: false, data: null } as AddressState),
    );

    this.hasAddress$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(hasAddress => this.checkoutState.setDeliveryAddressValid(hasAddress));
  }

  get address$(): Observable<B2BUnitAddress | null> {
    return this.addressState$.pipe(map(s => s.data));
  }

  /** Emite true quando o endereço foi carregado e não é nulo. */
  get hasAddress$(): Observable<boolean> {
    return this.addressState$.pipe(map(s => s.loaded && s.data !== null));
  }

  get fullName(): Observable<string> {
    return this.address$.pipe(
      map(address => {
        if (!address) return '';
        const parts = [address.firstname, address.lastname].filter(Boolean);
        return parts.join(' ');
      }),
    );
  }

  get addressLine(): Observable<string> {
    return this.address$.pipe(
      map(address => {
        if (!address) return '';
        const parts = [address.streetname, address.streetnumber].filter(Boolean);
        const street = parts.join(', ');
        return address.complement ? `${street} – ${address.complement}` : street;
      }),
    );
  }

  get cityStateLine(): Observable<string> {
    return this.address$.pipe(
      map(address => {
        if (!address) return '';
        const parts = [address.town, address.postalcode].filter(Boolean);
        return parts.join(', ');
      }),
    );
  }
}
